import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { MapPin, Pencil } from "lucide-react";

import { PwaPushSettings } from "@/pwa/PushUi";
import { PwaInstall } from "@/pwa/PwaUi";
import { ApiError, api } from "@/api/client";
import { useAuth } from "@/app/auth";
import { useMkTheme } from "@/app/branding";
import { useToast } from "@/app/toast";
import { PmLocationModal } from "@/pm/PmLocationModal";
import { useUserLocation } from "@/pm/useUserLocation";

import { MkAvatarEditor } from "./MkAvatarEditor";
import { MkButton } from "./MkButton";
import { MkChoice } from "./MkChoice";
import { MkInput } from "./MkInput";
import { MkModal } from "./MkModal";
import { MkSignature } from "./MkSignature";
import { MkSkeleton } from "./MkSkeleton";
import { MkThemeSwitcher, densityOptions } from "./MkThemeSwitcher";

interface Profile {
    id: number;
    name: string;
    email: string;
    login: string;
    lang: string;
    pm_density: string;
    has_avatar: boolean;
    has_signature: boolean;
    languages: { code: string; name: string }[];
    companies: { id: number; name: string }[];
    active_company_id: number;
}

interface Draft {
    name: string;
    email: string;
    lang: string;
    pm_density: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function MkProfileModal({ open, onClose }: { open: boolean; onClose(): void }) {
    const { t } = useTranslation();
    const toast = useToast();
    const { chooseCompany, chooseLanguage } = useAuth();
    const { setDensity } = useMkTheme();
    const [requested, setRequested] = useState(false);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
    const [saving, setSaving] = useState(false);
    const [companyId, setCompanyId] = useState<number | null>(null);
    const [avatar, setAvatar] = useState<string | false>(false);
    const [hasAvatar, setHasAvatar] = useState(false);
    const [avatarTouched, setAvatarTouched] = useState(false);
    const [signature, setSignature] = useState<string | false>(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [signatureTouched, setSignatureTouched] = useState(false);
    const [locationOpen, setLocationOpen] = useState(false);
    const { location, loading: locationLoading } = useUserLocation({ autoDetect: false });

    useEffect(() => {
        if (open) {
            setRequested(true);
        }
    }, [open]);

    const profile = useQuery<{ data: Profile }>({
        queryKey: ["me", "profile"],
        queryFn: () => api.raw<Profile>("/me/profile"),
        enabled: requested,
        staleTime: Infinity,
    });

    const data = profile.data?.data;

    useEffect(() => {
        if (!data || draft) {
            return;
        }
        setDraft({
            name: data.name || "",
            email: data.email || "",
            lang: data.lang,
            pm_density: ["comfortable", "compact"].includes(data.pm_density)
                ? data.pm_density : "comfortable",
        });
        setHasAvatar(data.has_avatar);
        setHasSignature(data.has_signature);
        setCompanyId(data.active_company_id);
    }, [data, draft]);

    function set<K extends keyof Draft>(key: K, value: Draft[K]) {
        setDraft((current) => (current ? { ...current, [key]: value } : current));
        if (key === "name" || key === "email") {
            setErrors((current) => ({ ...current, [key]: "" }));
        }
    }

    const signatureSrc = signature
        ? signature
        : (signatureTouched || !hasSignature ? "" : "/api/v1/me/signature");

    async function save() {
        if (!draft || !data) {
            return;
        }
        const next = {
            name: draft.name.trim() ? "" : t("mk.validation.required"),
            email: !draft.email.trim() ? t("mk.validation.required")
                : (EMAIL_RE.test(draft.email) ? "" : t("mk.validation.email")),
        };
        setErrors(next);
        if (next.name || next.email) {
            return;
        }
        setSaving(true);
        const values: Record<string, unknown> = {
            name: draft.name,
            email: draft.email,
            pm_density: draft.pm_density,
        };
        if (avatarTouched) {
            values.image_1920 = avatar ? String(avatar).split(",")[1] || false : false;
        }
        if (signatureTouched) {
            values.pm_signature = signature ? String(signature).split(",")[1] || false : false;
        }
        try {
            await api.patch("/me/profile", values);
            setDensity(draft.pm_density);
            if (companyId && companyId !== data.active_company_id) {
                await chooseCompany(companyId);
                window.location.reload();
                return;
            }
            const langChanged = Boolean(draft.lang) && draft.lang !== data.lang;
            if (langChanged) {
                await chooseLanguage(draft.lang);
            }
            if (langChanged || avatarTouched) {
                window.location.reload();
                return;
            }
            toast.success(t("mk.profile.updated"));
            onClose();
        } catch (error) {
            toast.danger(error instanceof ApiError && error.code === "offline" ? error.message : t("mk.profile.saveFailed"));
        } finally {
            setSaving(false);
        }
    }

    const companies = (data?.companies ?? []).map((company) => ({
        id: company.id, label: company.name,
    }));
    const languages = (data?.languages ?? []).map((lang) => ({ id: lang.code, label: lang.name }));

    return (
        <MkModal open={open} size="md" onClose={onClose}
                 title={t("mk.profile.title")} subtitle={data?.login} icon="fa-user-circle"
                 footer={(
                     <>
                         <MkButton variant="ghost" label={t("mk.cancel")} onClick={onClose} />
                         <MkButton variant="primary" label={t("mk.saveChanges")} icon="fa-check"
                                   loading={saving} onClick={() => void save()} />
                     </>
                 )}>
            <div className="mk-profile-modal">
                <PwaInstall />
                <PwaPushSettings />
                {!draft || !data ? (
                    <>
                        <div className="mk-profile-head">
                            <MkSkeleton variant="circle" width="84px" height="84px" />
                            <div className="mk-flex-1">
                                <MkSkeleton width="60%" />
                                <MkSkeleton width="40%" />
                            </div>
                        </div>
                        <MkSkeleton lines={4} />
                    </>
                ) : (
                    <>
                        <div className="mk-profile-head">
                            <MkAvatarEditor size="xl"
                                            src={`/api/v1/files/image/res.users/${data.id}/avatar_256`}
                                            value={avatar} hasImage={hasAvatar} name={draft.name}
                                            title={t("mk.profile.picture")}
                                            onUpload={(url) => {
                                                setAvatar(url);
                                                setHasAvatar(true);
                                                setAvatarTouched(true);
                                            }}
                                            onClear={() => {
                                                setAvatar(false);
                                                setHasAvatar(false);
                                                setAvatarTouched(true);
                                            }} />
                            <div className="mk-flex-1">
                                <div className="mk-profile-name">{draft.name}</div>
                                <div className="mk-profile-mail">{draft.email}</div>
                            </div>
                        </div>

                        <div className="mk-form-grid">
                            <MkInput label={t("mk.profile.name")} value={draft.name} required
                                     error={errors.name} onChange={(value) => set("name", value)} />
                            <MkInput label={t("mk.profile.email")} type="email" value={draft.email}
                                     required error={errors.email}
                                     onChange={(value) => set("email", value)} />
                        </div>

                        <MkSignature label={t("mk.profile.signature")}
                                     hint={t("mk.profile.signatureHint")}
                                     value={signature} src={signatureSrc}
                                     onUpdate={(url) => {
                                         setSignature(url || false);
                                         setHasSignature(Boolean(url));
                                         setSignatureTouched(true);
                                     }} />

                        <div className="mk-profile-prefs">
                            {companies.length > 1 && (
                                <div className="mk-profile-company">
                                    <MkChoice label={t("mk.profile.company")} wrap options={companies}
                                              value={companyId ?? undefined}
                                              onChange={(value) => setCompanyId(value)} />
                                </div>
                            )}
                            <MkChoice label={t("mk.profile.language")} wrap options={languages}
                                      value={draft.lang} onChange={(value) => set("lang", value)} />
                            <div className="mk-choice-field">
                                <label className="mk-field-label">{t("mk.profile.city")}</label>
                                <button type="button" className="mk-input-wrap mk-profile-location"
                                        title={t("home.location.change")}
                                        aria-haspopup="dialog"
                                        onClick={() => setLocationOpen(true)}>
                                    <MapPin className="mk-input-icon" size={16} aria-hidden="true" />
                                    <span className="mk-profile-location-text">
                                        {locationLoading
                                            ? t("home.location.loading")
                                            : location?.city || t("home.location.set")}
                                    </span>
                                    <Pencil className="mk-profile-location-edit" size={14}
                                            aria-hidden="true" />
                                </button>
                            </div>
                            <MkChoice label={t("mk.profile.density")} options={densityOptions(t)}
                                      value={draft.pm_density}
                                      onChange={(value) => set("pm_density", value)} />
                            <div className="mk-profile-theme">
                                <label className="mk-field-label">{t("mk.profile.theme")}</label>
                                <MkThemeSwitcher />
                            </div>
                        </div>
                    </>
                )}
                <PmLocationModal open={locationOpen} current={location}
                                 onClose={() => setLocationOpen(false)} />
            </div>
        </MkModal>
    );
}
